/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2018
 */

/*global angular*/
import { SuccessService } from './../../../../../services/success.service';
import { UserService } from './../../../../../services/user.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { EnumsService } from '../../../../../services/enum.service';
import { tap, catchError, of } from 'rxjs';
import { NexleaderEcourseRaliVideoComponent } from '../../../../ecourse/components/ecourse-rali-video/ecourse-rali-video.component';
import { environment } from '../../../../../../environments/environment';
import { NexleaderOnboardingPassionsComponent } from '../../onboarding/onboarding-preassessments/onboarding-passions/onboarding-passions.component';
import { NexleaderPreIpsatChecklistComponent } from '../../onboarding/onboarding-preassessments/pre-ipsat-checklist/pre-ipsat-checklist.component';

@Component({
  selector: 'app-nexleader-ecourse-module-six',
  standalone: true,
  imports: [CommonModule, NexleaderEcourseRaliVideoComponent, NexleaderOnboardingPassionsComponent, NexleaderPreIpsatChecklistComponent],
  templateUrl: './ecourse-module-six.component.html'
})
export class NexleaderEcourseModuleSixComponent implements OnInit {
  /**
   * handleNextStep
   *
   * Allows data that is edited on this ecourse module to be saved.
   */
  @Output() handleNextStep = new EventEmitter();

  /**
   * user
   *
   * A user object we can reference to and update as the onboarding user goes throughout the process.
   */
  @Input() user: any;

  currentPrerequisite: any;

  /**
   * moduleSteps
   * 
   * Controls the steps the user can move between for the ecourse module.
   */
  moduleSteps: string[] = ['engagement', 'assessment'];

  /**
   * currentStep
   * 
   * Determines the current step the user is on in the ecourse module.
   */
  currentStep: string = this.moduleSteps[0];

  constructor(
    private successService: SuccessService,
    private userService: UserService,
    private errorHandler: ErrorHandlerService,
    private enumsService: EnumsService) { }

  ngOnInit(): void {
    this.load();

    // Automatically move the user to the engagement step if they've already completed the MPP
    if (this.user && this.user.prerequisites.completedPassions) {
      this.nextStep();
    }
  }

  /**
   * load()
   * 
   * Loads all required enums for the module.
   */
  load(): void {
    this.enumsService.getEnums().pipe(
      tap((enums) => {
        this.currentPrerequisite = enums.Prerequisites.PASSIONS;
      }), catchError((error) => {
        this.errorHandler.handleError(error);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * nextStep()
   * 
   * Handles moving the user to the next step (WITHIN THE MODULE).
   *  Calls the handleNextStep() function if the user is on the last step.
   *  ie: moving from learning_assessment to engagement
   */
  nextStep(): void {
    const currentStepIndex = this.moduleSteps.indexOf(this.currentStep);
    if (currentStepIndex < this.moduleSteps.length - 1) {
      this.currentStep = this.moduleSteps[currentStepIndex + 1];
    } else {
      this.save();
      this.handleNextStep.emit();
    }
  }

  /**
   * mapContinueButtonTextForStep()
   * 
   * Maps text for the continue buttons given the current step of the module the user
   *  is on.
   */
  mapContinueButtonTextForStep(): string | null {
    switch (this.currentStep) {
      case 'engagement':
        return 'Continue to MyPassionProfile';
      default:
        return null;
    }
  }

  /**
   * save()
   * 
   * Saves a user. To be called when an MBTI option is selected.
   */
  save(): void {
    this.user.prerequisites.completedPassions = true;
    this.userService
      .saveUser(this.user)
      .pipe(
        tap((_) => {
          this.successService.handle();
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * canProceedToNextStep()
   * 
   * Determines if the user is able to continue to the next module of the ecourse.
   */
  canProceedToNextStep(): boolean {
    return this.user &&
      this.user.prerequisites.completedPassions &&
      this.user.prerequisites.passions &&
      this.user.prerequisites.passions.length === 3 &&
      this.user.prerequisites.passions.every(this.nonemptyStringObj) &&
      this.user.prerequisites.passions.every((passion: any) => passion.level && passion.level.name);
  }

  /**
   * nonemptyStringObj()
   * 
   * helper function
   * 
   * Determines if the object contains a key of name that has a string value.
   * 
   * @param {Object} strO - An object to check.
   */
  nonemptyStringObj(strO: any): boolean {
    if (!strO) return false;
    return typeof strO.name === 'string' && strO.name.trim() !== '';
  }
}
